import { useEffect, useState } from 'react';
import { financialConfigApi, sitesApi } from '@verlo/shared/services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './Section';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Separator } from '@verlo/shared/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { toast } from 'sonner';
import { SitePicker } from '@verlo/shared/components/ui/site-picker';
import { TimeField } from '@verlo/shared/components/ui/time-field';

// Backend uses 0 = Mon … 6 = Sun (Python's date.weekday()).
const WEEK_START_OPTIONS = [
  ['0', 'Monday'], ['1', 'Tuesday'], ['2', 'Wednesday'], ['3', 'Thursday'],
  ['4', 'Friday'], ['5', 'Saturday'], ['6', 'Sunday'],
];
const STRUCTURES = [['4-4-5', '4-4-5'], ['4-5-4', '4-5-4'], ['5-4-4', '5-4-4'], ['13x4', '13 × 4 weeks']];
const YEAR_RULES = [
  ['nearest', 'the week start nearest the anchor date (52 or 53 weeks)'],
  ['last', 'the last week start on or before the anchor date (52 or 53 weeks)'],
  ['fixed_52', 'exactly 52 weeks after the previous year'],
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const FinancialCalendarCard = () => {
  const [cfg, setCfg] = useState(null);
  const [site, setSite] = useState('');
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);

  // Daily budget entry
  const [budgetDate, setBudgetDate] = useState(todayISO());
  const [budgetAmount, setBudgetAmount] = useState('');
  const [budgetSaving, setBudgetSaving] = useState(false);
  const [recent, setRecent] = useState([]);

  const load = async (siteId) => {
    const { data } = await financialConfigApi.getSettings(siteId);
    setCfg(data);
  };

  const loadRecent = async (siteId) => {
    if (!siteId) return;
    const from = new Date(Date.now() - 3 * 864e5).toISOString().slice(0, 10);
    const to = new Date(Date.now() + 10 * 864e5).toISOString().slice(0, 10);
    const { data } = await financialConfigApi.listBudgets({
      site: siteId, period_type: 'day', metric: 'net_sales', from, to,
    });
    setRecent(data.rows || []);
  };

  useEffect(() => {
    sitesApi.list()
      .then(({ data }) => {
        setSites(data || []);
        const first = data?.[0]?.id || '';
        setSite(first);
      })
      .catch(() => toast.error('Could not load sites'));
  }, []);

  useEffect(() => {
    if (!site) { load(undefined).catch(() => toast.error('Could not load financial settings')); return; }
    load(site).catch(() => toast.error('Could not load financial settings'));
    loadRecent(site).catch(() => {});
  }, [site]);

  if (!cfg) return null;

  const set = (patch) => setCfg({ ...cfg, ...patch });
  const setVenue = (patch) => setCfg({
    ...cfg,
    venues: { ...(cfg.venues || {}), [site]: { ...((cfg.venues || {})[site] || {}), ...patch } },
  });
  const venue = (cfg.venues || {})[site] || {};

  const save = async () => {
    if (cfg.period_model === 'accounting' && !cfg.accounting_year_start) {
      toast.error('An accounting calendar needs an anchor date');
      return;
    }
    setSaving(true);
    try {
      const patch = {
        week_start_day: Number(cfg.week_start_day),
        period_model: cfg.period_model,
        accounting_structure: cfg.accounting_structure,
        accounting_year_start: cfg.period_model === 'accounting' ? cfg.accounting_year_start : null,
        accounting_year_rule: cfg.accounting_year_rule || 'nearest',
        default_trading_day_start: cfg.default_trading_day_start,
        labour_cost_basis: cfg.labour_cost_basis,
        default_on_cost_pct: Number(cfg.default_on_cost_pct),
        default_late_after_minutes: Number(cfg.default_late_after_minutes ?? 15),
        default_turn_times: Object.fromEntries(Object.entries(cfg.default_turn_times || {}).map(([k, v]) => [k, Number(v) || 90])),
      };
      if (site) {
        patch.venues = {
          [site]: {
            trading_day_start: venue.trading_day_start || null,
            on_cost_pct: venue.on_cost_pct != null && venue.on_cost_pct !== '' ? Number(venue.on_cost_pct) : null,
            late_after_minutes: venue.late_after_minutes != null && venue.late_after_minutes !== '' ? Number(venue.late_after_minutes) : null,
          },
        };
      }
      const { data } = await financialConfigApi.updateSettings(patch);
      setCfg(data);
      toast.success('Financial calendar saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the financial calendar');
    } finally {
      setSaving(false);
    }
  };

  const saveBudget = async () => {
    const amt = parseFloat(budgetAmount);
    if (!site) return;
    if (!(amt >= 0)) { toast.error('Enter a budget amount'); return; }
    setBudgetSaving(true);
    try {
      await financialConfigApi.setBudget({
        site, date: budgetDate, period_type: 'day', metric: 'net_sales', amount: amt,
      });
      setBudgetAmount('');
      toast.success(`Daily budget set for ${budgetDate}`);
      loadRecent(site);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the budget');
    } finally {
      setBudgetSaving(false);
    }
  };

  const fmt = (n) => `£${Number(n || 0).toLocaleString('en-GB')}`;

  return (
    <Card data-testid="financial-calendar-card">
      <CardHeader>
        <CardTitle>Financial calendar</CardTitle>
        <CardDescription>
          One definition of the trading day, the week and the period, used by Verlo Service,
          shift reporting and back-office reporting alike.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {sites.length > 1 && (
          <div className="space-y-1.5 max-w-xs">
            <Label className="text-xs">Venue</Label>
            <SitePicker sites={sites} value={site} onChange={setSite} testId="fc-site" />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Week starts on</Label>
            <Select value={String(cfg.week_start_day)} onValueChange={(v) => set({ week_start_day: v })}>
              <SelectTrigger data-testid="fc-week-start"><SelectValue /></SelectTrigger>
              <SelectContent>
                {WEEK_START_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Period model</Label>
            <Select value={cfg.period_model} onValueChange={(v) => set({ period_model: v })}>
              <SelectTrigger data-testid="fc-period-model"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="calendar">Calendar (day / week / month)</SelectItem>
                <SelectItem value="accounting">Accounting periods (4-4-5 etc.)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fc-tds" className="text-xs">Trading day starts at</Label>
            <TimeField
              id="fc-tds"
              value={cfg.default_trading_day_start || '05:00'}
              onChange={(t) => set({ default_trading_day_start: t })}
              testId="fc-trading-day-start"
            />
          </div>
        </div>

        {cfg.period_model === 'accounting' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Structure</Label>
              <Select value={cfg.accounting_structure || '4-4-5'} onValueChange={(v) => set({ accounting_structure: v })}>
                <SelectTrigger data-testid="fc-structure"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STRUCTURES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fc-anchor" className="text-xs">Accounting year 1 starts</Label>
              <Input
                id="fc-anchor" type="date"
                value={cfg.accounting_year_start || ''}
                onChange={(e) => set({ accounting_year_start: e.target.value })}
                data-testid="fc-anchor"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Each year starts on</Label>
              <Select value={cfg.accounting_year_rule || 'nearest'} onValueChange={(v) => set({ accounting_year_rule: v })}>
                <SelectTrigger data-testid="fc-year-rule"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {YEAR_RULES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-ink-faint">
                A retail year is 364 days, so every five or six years one runs to 53 weeks; the extra week goes on the last period. "Nearest" and "last" pin each year to the anchor's date; "exactly 52" lets it drift.
              </p>
            </div>
          </div>
        )}

        <Separator />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Labour cost basis</Label>
            <Select value={cfg.labour_cost_basis} onValueChange={(v) => set({ labour_cost_basis: v })}>
              <SelectTrigger data-testid="fc-labour-basis"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gross">Gross pay only</SelectItem>
                <SelectItem value="gross_plus_oncosts">Gross + on-costs (true cost)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fc-oncost" className="text-xs">On-cost % (employer NI, pension, holiday)</Label>
            <Input
              id="fc-oncost" type="number" min="0" max="100" step="0.5"
              value={Math.round((Number(cfg.default_on_cost_pct) || 0) * 1000) / 10}
              onChange={(e) => set({ default_on_cost_pct: (parseFloat(e.target.value) || 0) / 100 })}
              data-testid="fc-oncost"
            />
          </div>
        </div>

        <Separator />

        {/* Bookings — what "late" means, and how long a party holds a table (B4). */}
        <div className="space-y-3">
          <p className="text-xs font-medium text-ink-soft">Bookings</p>
          {/* Bottom-aligned: the turn times carry a row of party sizes over their boxes, and
              their boxes should line up with Late after's. */}
          <div className="grid grid-cols-1 md:grid-cols-2 md:items-end gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="fc-late" className="text-xs">Late after (minutes past the booking time)</Label>
              <Input
                id="fc-late" type="number" min="0" max="180" step="5"
                value={cfg.default_late_after_minutes ?? 15}
                onChange={(e) => set({ default_late_after_minutes: parseInt(e.target.value, 10) || 0 })}
                data-testid="fc-late-after"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Turn time by party size (minutes)</Label>
              <div className="grid grid-cols-4 gap-2">
                {['2', '4', '6', '8'].map((size) => (
                  <div key={size} className="space-y-1">
                    <span className="block text-[11px] text-ink-faint text-center">{size === '8' ? '7+' : `≤${size}`}</span>
                    <Input
                      type="number" min="15" max="360" step="5"
                      value={(cfg.default_turn_times || {})[size] ?? ''}
                      onChange={(e) => set({ default_turn_times: { ...(cfg.default_turn_times || {}), [size]: parseInt(e.target.value, 10) || 0 } })}
                      className="text-center"
                      data-testid={`fc-turn-${size}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {site && (
          <div className="rounded-lg border bg-surface-2 p-3 space-y-3">
            <p className="text-xs font-medium text-ink-soft">
              Override for {sites.find((s) => s.id === site)?.name || 'this venue'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="fc-v-tds" className="text-xs">Trading day starts at</Label>
                <TimeField
                  id="fc-v-tds" allowEmpty
                  value={venue.trading_day_start || ''}
                  onChange={(t) => setVenue({ trading_day_start: t })}
                  placeholder={cfg.default_trading_day_start}
                  testId="fc-venue-tds"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fc-v-oncost" className="text-xs">On-cost %</Label>
                <Input
                  id="fc-v-oncost" type="number" min="0" max="100" step="0.5"
                  value={venue.on_cost_pct != null && venue.on_cost_pct !== '' ? Math.round(Number(venue.on_cost_pct) * 1000) / 10 : ''}
                  onChange={(e) => setVenue({ on_cost_pct: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0) / 100 })}
                  placeholder={String(Math.round((Number(cfg.default_on_cost_pct) || 0) * 1000) / 10)}
                  data-testid="fc-venue-oncost"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fc-v-late" className="text-xs">Late after (minutes)</Label>
                <Input
                  id="fc-v-late" type="number" min="0" max="180" step="5"
                  value={venue.late_after_minutes ?? ''}
                  onChange={(e) => setVenue({ late_after_minutes: e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0 })}
                  placeholder={String(cfg.default_late_after_minutes ?? 15)}
                  data-testid="fc-venue-late"
                />
              </div>
            </div>
            <p className="text-[11px] text-ink-faint">Leave blank to use the business default.</p>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="fc-save" className="bg-verlo hover:bg-verlo-hover">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>

        <Separator />

        {/* Daily budget entry point (6a). The weekly / monthly / period authoring surface is 6c. */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <p className="font-medium text-sm">Daily budget</p>
          </div>
          <p className="text-xs text-ink-soft">
            The net-sales figure the team expects for a trading day. Verlo Service shows actual against this.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="b-date" className="text-xs">Trading day</Label>
              <Input id="b-date" type="date" value={budgetDate}
                onChange={(e) => setBudgetDate(e.target.value)}
                className="w-44" data-testid="budget-date" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="b-amt" className="text-xs">Net sales budget (£)</Label>
              <Input id="b-amt" type="number" min="0" step="50" placeholder="e.g. 4200"
                value={budgetAmount} onChange={(e) => setBudgetAmount(e.target.value)}
                className="w-40" data-testid="budget-amount" />
            </div>
            <Button variant="outline" onClick={saveBudget} disabled={budgetSaving} data-testid="budget-save">
              {budgetSaving ? 'Saving…' : 'Save budget'}
            </Button>
          </div>
          {recent.length > 0 && (
            <div className="text-xs text-ink-soft divide-y rounded-lg border">
              {recent.map((r) => (
                <div key={r.id} className="flex justify-between px-3 py-1.5" data-testid={`budget-row-${r.period_start}`}>
                  <span>{r.period_start}</span>
                  <span className="font-medium font-mono">{fmt(r.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FinancialCalendarCard;
